import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { NotPermitted, StatusChip, type ChipTone } from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { TestInboxField, useTestInbox, type Can } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import { accountsApi } from '../api/accounts-api';
import type {
  IAdminAccountDetail,
  IAdminAccountRow,
  IAdminIdpInvitation,
  IdpInvitationReason,
  IdpInvitationSource,
  IdpInvitationStatus
} from '../api/account-contracts';
import { isNotFound, unanswered } from '../pages/person-hub/said';
import { neverArrived, statusRefusal } from '../refusal';
import { AccountStateChip, absent } from './account-facts';
import { Fact, useAdminData } from './admin-page';
import { CopyableValue } from './copyable-value';

/**
 * How an account signs in (SR-476, the IdP sign-in design's decision 11): the identity it signs
 * in with at Invent's IdP, its invitations to sign in, and the two writes on them — "Send
 * invitation" and "Unlink". Drawn on two screens, the user card (`user-detail-page.tsx`) and the
 * person hub's Account & access for a viewer holding `accounts.browse` (`credentials.tsx`), and
 * written once here, so the rules the writes carry cannot come to hold on one screen and not the
 * other. Each screen reads the account through `GET …/accounts/by-email/{email}` itself, because
 * that read is the only one carrying `idp`; the hub's own document stops short of it.
 *
 * **The invitations are the card's own read** (`GET …/accounts/{accountId}/idp-invitations`,
 * `accounts.browse`, the screen's own policy), newest first, read again after each write here and
 * whenever the account read changes — and every 5 s while the newest is `queued` and the tab is
 * shown, for two minutes at most: the worker sends it, and it turns `pending` with its redeem link,
 * with no write here to say so. A live `pending` row carries the IdP's redeem link, shown in every
 * environment so staff can hand it over when the mail does not arrive (IDP §5) — to the holder of
 * its address alone: nobody else can redeem it, and on dev and UAT that holder is the tester,
 * never the advisor (§9).
 *
 * **Drawn off, never hidden, with the reason** (decision 11): the caller's own account (survey-api's
 * `own-account`), a caller without `accounts.manage`, a Team Portal account for a caller without
 * `roles.manage`; and for the send, a disabled account and a linked one. Each of those refusals is
 * still said when a page read before the account moved meets it. Whether the account holds a
 * permission the caller lacks is survey-api's to judge (D5): the controls are offered, and
 * `permission-not-held` is said.
 *
 * **What comes back is said under the controls**: a landing, a refusal, or an answer that never
 * arrived — after which the account and its invitations are read again, and the note says to
 * check them before pressing again.
 */

/**
 * `isSelf` is survey-api's own refusal — every account write 403s on the caller's own account —
 * a fact of identity, not of authority. The address is compared, as `account.email` is `citext`.
 * The rest is the writes' permissions, read so that a screen opened to `accounts.browse` draws a
 * control it cannot press disabled, with the reason. Shared with the roles card beside this one.
 */
export interface IAccountPowers {
  isSelf: boolean;
  /** `accounts.manage` — the invitation, the unlink and the status. */
  canManage: boolean;
  /** `roles.manage` — the roles, and asked besides for a Team Portal account's writes. */
  canGrant: boolean;
}

export function accountPowers(
  can: Can,
  signedInEmail: Nullable<string> | undefined,
  account: Nullable<IAdminAccountRow>
): IAccountPowers {
  return {
    isSelf: !!account && signedInEmail?.toLowerCase() === account.email.toLowerCase(),
    canManage: can('accounts.manage'),
    canGrant: can('roles.manage')
  };
}

interface IStatusWord {
  word: string;
  tone: ChipTone;
}

const STATUS_WORDS: Record<IdpInvitationStatus, IStatusWord> = {
  queued: { word: 'Queued', tone: 'info' },
  pending: { word: 'Pending', tone: 'info' },
  accepted: { word: 'Accepted', tone: 'success' },
  superseded: { word: 'Superseded', tone: 'neutral' },
  withheld: { word: 'Withheld', tone: 'attention' },
  failed: { word: 'Failed', tone: 'danger' },
  expired: { word: 'Expired', tone: 'neutral' }
};

const SOURCE_WORDS: Record<IdpInvitationSource, string> = {
  staff: 'Sent by staff',
  seat: 'With the new seat',
  registration: 'At registration',
  request: 'Asked for by its owner',
  contact: 'With a contact’s acceptance',
  bootstrap: 'The bootstrap seat'
};

const REASON_WORDS: Record<IdpInvitationReason, string> = {
  not_allowlisted: 'the address is outside the allowed domains',
  account_disabled: 'the account is disabled',
  idp_rejected: 'the IdP refused it',
  idp_not_configured: 'no IdP directory is configured',
  address_taken: 'another account holds the address'
};

/** A word from one of the tables above, or the value as sent — a spelling survey-api adds
 *  before this app's mirror moves. */
function wordOf(table: Readonly<Record<string, string>>, value: string): string {
  return table[value] ?? value;
}

/** The bodiless 403 both writes can meet: the policy's — `accounts.manage` gone, or a Team Portal
 *  account and `roles.manage` gone. A 404 is the account gone, said by the screen's re-read. */
const SIGN_IN_REFUSALS: Record<number, string> = {
  403:
    'The system refused this — your Team Portal roles do not include managing accounts, or, for ' +
    'a Team Portal account, managing roles. The page is read again with your current roles.'
};

/** A refusal both writes share, told by type first. `null` for one the caller words itself. */
function sharedRefusal(error: unknown): Nullable<string> {
  if (!(error instanceof ApiError)) return null;
  if (error.isProblem(ProblemType.ownAccount)) {
    return 'The system refused this: it is your own account. Ask another administrator.';
  }
  if (error.isProblem(ProblemType.permissionNotHeld)) {
    return (
      'The system refused this: the account holds a permission your Team Portal roles do not, ' +
      'so its sign-in is for someone holding at least as much. Nothing was changed.'
    );
  }
  return null;
}

interface ISaid {
  tone: 'done' | 'refused';
  text: string;
}

/** How often, and how many times, a `queued` newest invitation is read again: two minutes. */
const QUEUED_REREAD_MS = 5_000;
const QUEUED_REREADS = 24;

export function SignInCard({
  account,
  powers,
  onReread
}: {
  account: IAdminAccountDetail;
  powers: IAccountPowers;
  /** The screen's re-read of the account — after a write, and after a refusal that says the
   *  account moved. */
  onReread: () => void;
}) {
  const ids = { inbox: React.useId(), reason: React.useId() };
  const inbox = useTestInbox();
  const [busy, setBusy] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const [said, setSaid] = React.useState<Nullable<ISaid>>(null);
  const invitations = useAdminData<IAdminIdpInvitation[]>(
    (signal) => accountsApi.listIdpInvitations(account.accountId, signal),
    [account.accountId, account.updatedAt]
  );

  const rereadBoth = () => {
    onReread();
    void invitations.reload();
  };

  // Each queued invitation gets its own two minutes; one that moves on, or a newer one, ends them.
  const newest = invitations.data?.[0];
  const queuedId = newest?.status === 'queued' ? newest.invitationId : null;
  const { reload } = invitations;
  React.useEffect(() => {
    if (queuedId === null) return;
    let rereads = 0;
    const timer = window.setInterval(() => {
      rereads += 1;
      if (rereads >= QUEUED_REREADS) window.clearInterval(timer);
      // Not for a hidden tab, as the person hub's own re-reads.
      if (document.visibilityState !== 'hidden') void reload();
    }, QUEUED_REREAD_MS);
    return () => window.clearInterval(timer);
  }, [queuedId, reload]);

  // One reason for both writes, then the send's own two — said once, under the controls.
  let offRule: Nullable<React.ReactNode> = null;
  if (powers.isSelf) {
    offRule = <OffReason>This is your own account — the system refuses it.</OffReason>;
  } else if (!powers.canManage) {
    offRule = <NotPermitted what="inviting an account or unlinking its identity" />;
  } else if (account.kind === 'staff' && !powers.canGrant) {
    offRule = (
      <NotPermitted what="managing roles, which a Team Portal account’s sign-in asks as well" />
    );
  }
  let sendOff = offRule;
  if (sendOff === null && account.status === 'disabled') {
    sendOff = (
      <OffReason>
        The account is disabled — an invitation would work for nobody until it is active again.
      </OffReason>
    );
  } else if (sendOff === null && account.idp !== null) {
    sendOff = (
      <OffReason>It signs in already. Unlink its identity first to invite another.</OffReason>
    );
  }

  const send = async () => {
    setBusy(true);
    setSaid(null);
    inbox.begin();
    const to = inbox.body.deliverTo ?? account.email;
    try {
      await accountsApi.inviteToIdp(account.accountId, inbox.body);
      setSaid({
        tone: 'done',
        text:
          `Queued for ${to}${inbox.body.deliverTo ? ' (the test inbox)' : ''}: the system sends ` +
          'it. Where an invitation was live at that address, it is sent again.'
      });
      void invitations.reload();
    } catch (error) {
      if (inbox.refused(error, account.email)) return;
      if (unanswered(error)) {
        console.warn('[Admin] The invitation got no answer:', error);
        setSaid({
          tone: 'refused',
          text: neverArrived(
            'the invitation',
            'The account and its invitations are read again — check them before sending again.'
          )
        });
        rereadBoth();
        return;
      }
      if (isNotFound(error)) {
        onReread();
        return;
      }
      setSaid({ tone: 'refused', text: sendRefusal(error, to) });
      if (
        error instanceof ApiError &&
        (error.isProblem(ProblemType.accountDisabled) ||
          error.isProblem(ProblemType.idpAlreadyLinked))
      ) {
        rereadBoth();
      }
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    setBusy(true);
    setSaid(null);
    try {
      await accountsApi.unlinkIdp(account.accountId);
      setConfirming(false);
      setSaid({
        tone: 'done',
        text:
          'Unlinked. Every session of the account ended, and its live invitation was superseded. ' +
          'Its owner signs in again through a new invitation.'
      });
      rereadBoth();
    } catch (error) {
      setConfirming(false);
      if (unanswered(error)) {
        console.warn('[Admin] The unlink got no answer:', error);
        setSaid({
          tone: 'refused',
          text: neverArrived(
            'the unlink',
            'The account is read again — check its identity before unlinking again.'
          )
        });
        rereadBoth();
        return;
      }
      if (isNotFound(error)) {
        onReread();
        return;
      }
      setSaid({
        tone: 'refused',
        text: sharedRefusal(error) ?? statusRefusal(error, SIGN_IN_REFUSALS, 'Could not unlink')
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-sign-in-card>
      <CardHeader>
        <CardTitle>Sign-in</CardTitle>
        <CardDescription>
          How this account signs in at Invent, and its invitations to sign in.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 text-sm">
        <div data-idp-identity>
          <Fact
            label="Identity"
            value={
              account.idp ? (
                <>
                  <span className="break-all">{account.idp.email}</span>
                  <span className="mt-1 block text-muted-foreground">
                    Linked {formatDateTime(account.idp.linkedAt)}.
                  </span>
                </>
              ) : (
                <>
                  <AccountStateChip state="not linked" />
                  <span className="mt-1 block text-muted-foreground">
                    Its first sign-in at Invent links an identity, through a live invitation.
                  </span>
                </>
              )
            }
          />
        </div>

        <InvitationList invitations={invitations.data} error={invitations.error} />

        <div className="grid gap-3">
          <TestInboxField
            inbox={inbox}
            id={ids.inbox}
            intended={account.email}
            disabled={busy || sendOff !== null}
          />
          <div className="flex flex-wrap items-start gap-3">
            <Button
              type="button"
              onClick={() => void send()}
              disabled={busy || sendOff !== null || !inbox.settled}
              aria-describedby={sendOff === null ? undefined : ids.reason}
            >
              {busy && !confirming ? 'Sending…' : 'Send invitation'}
            </Button>
            {account.idp && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirming(true)}
                disabled={busy || offRule !== null}
                aria-describedby={offRule === null ? undefined : ids.reason}
              >
                Unlink…
              </Button>
            )}
          </div>
          {/* `offRule`, where there is one, is `sendOff` too: one sentence serves both. */}
          {sendOff !== null && <div id={ids.reason}>{sendOff}</div>}
        </div>

        {said && (
          <p
            role={said.tone === 'refused' ? 'alert' : 'status'}
            data-sign-in-said
            className={said.tone === 'refused' ? 'text-destructive' : 'text-muted-foreground'}
          >
            {said.text}
          </p>
        )}

        <AlertDialog open={confirming} onOpenChange={(open) => !busy && setConfirming(open)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Unlink {account.idp?.email}?</AlertDialogTitle>
              <AlertDialogDescription>
                Every session of the account ends now, and nobody signs in as it until a new
                invitation links an identity. The identity stays at Invent: signing in with it
                again links whichever account then holds a live invitation for its address.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={busy}
                onClick={(event) => {
                  event.preventDefault();
                  void unlink();
                }}
              >
                {busy ? 'Unlinking…' : 'Unlink'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}

/** Why a control is drawn off, where the reason is not the reader's authority. */
function OffReason({ children }: { children: React.ReactNode }) {
  return (
    <p data-sign-in-off className="text-sm text-muted-foreground">
      {children}
    </p>
  );
}

/** The send's refusals, after the inbox's and the shared ones. */
function sendRefusal(error: unknown, to: string): string {
  if (error instanceof ApiError) {
    if (error.isProblem(ProblemType.accountDisabled)) {
      return (
        'The system refused this: the account is disabled, and an invitation would work for ' +
        'nobody until it is activated again. Nothing was sent. The account is read again.'
      );
    }
    if (error.isProblem(ProblemType.idpAlreadyLinked)) {
      return (
        'The system refused this: the account signs in already. Unlink its identity first. ' +
        'The account is read again.'
      );
    }
    if (error.isProblem(ProblemType.idpAddressTaken)) {
      return (
        `The system refused this: another account’s live invitation or identity holds ${to}. ` +
        'Nothing was sent.'
      );
    }
  }
  return (
    sharedRefusal(error) ??
    statusRefusal(error, SIGN_IN_REFUSALS, 'Could not send the invitation')
  );
}

/**
 * The invitations, newest first as survey-api orders them: the address the IdP invites — and the
 * account's address it was for, where the two differ — its status, what caused it, why it was
 * withheld or failed, and its dates; a live `pending` row's redeem link with Copy.
 */
function InvitationList({
  invitations,
  error
}: {
  invitations: Nullable<IAdminIdpInvitation[]>;
  error: Nullable<string>;
}) {
  if (invitations === null) {
    return error ? (
      <Alert variant="destructive">
        <AlertTitle>Could not read the invitations</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    ) : (
      <p className="text-muted-foreground">Reading the invitations…</p>
    );
  }

  return (
    <div className="grid gap-2">
      <span className="text-xs tracking-wide text-muted-foreground uppercase">Invitations</span>
      {error && (
        <Alert variant="destructive">
          <AlertTitle>Could not refresh — showing the invitations as last read</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {invitations.length === 0 ? (
        <p>{absent('None yet.')}</p>
      ) : (
        <ul aria-label="Invitations to sign in" className="grid gap-2">
          {invitations.map((invitation) => (
            <InvitationRow key={invitation.invitationId} invitation={invitation} />
          ))}
        </ul>
      )}
    </div>
  );
}

function InvitationRow({ invitation }: { invitation: IAdminIdpInvitation }) {
  const status: IStatusWord = (STATUS_WORDS as Record<string, IStatusWord>)[invitation.status] ?? {
    word: invitation.status,
    tone: 'neutral'
  };
  const redirected = invitation.address.toLowerCase() !== invitation.intended.toLowerCase();
  const facts = [
    wordOf(SOURCE_WORDS, invitation.source),
    `created ${formatDateTime(invitation.createdAt)}`,
    invitation.expiresAt && `expires ${formatDateTime(invitation.expiresAt)}`,
    invitation.reason && wordOf(REASON_WORDS, invitation.reason)
  ].filter(Boolean);

  return (
    <li
      data-idp-invitation={invitation.invitationId}
      data-status={invitation.status}
      className="grid gap-1 rounded-md border border-border p-3"
    >
      <span className="flex flex-wrap items-center gap-2">
        <span className="font-medium break-all">{invitation.address}</span>
        {redirected && (
          <span className="break-all text-muted-foreground">for {invitation.intended}</span>
        )}
        <StatusChip tone={status.tone}>{status.word}</StatusChip>
      </span>
      <span className="text-muted-foreground">{facts.join(' · ')}</span>
      {invitation.redeemUrl && (
        <div className="grid gap-1 pt-1">
          <CopyableValue label="Redeem link" value={invitation.redeemUrl} />
          <span className="text-muted-foreground">
            Hand it only to whoever holds {invitation.address} — nobody else can redeem it.
          </span>
        </div>
      )}
    </li>
  );
}
